import { GatedLink } from '@/components/Elements/Link/GatedLink';
import { Button } from '@/components/ui/elements/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/elements/card';
import { Separator } from '@/components/ui/elements/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/elements/tooltip';
import { PolicyGate } from '@/features/auth/authorization';
import { DateUsageIndicator } from '@/features/equipment/components/maintenance/DateUsageIndicator';
import { StatusIndicator } from '@/features/equipment/components/maintenance/StatusIndicator';
import { STATUS_COLOR_CLASSES } from '@/features/equipment/utils/statusColors';
import { Inspection } from '@/types';
import { cn } from '@/utils/format';
import { ArrowPathIcon, PlayIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';

export const InspectionCard = ({ inspection }: { inspection: Inspection }) => {
  const navigate = useNavigate();

  const logHistory =
    inspection.logs
      ?.filter((log) => log.performed_at !== null)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) ?? [];

  const ruleDisplay = inspection.rule_for_human?.split(', starting')[0];
  const statusIsOk = !inspection.is_past_due && !inspection.has_failed && !inspection.has_warning;
  const incompleteLog = logHistory.length > 0 && !logHistory[0].is_complete ? logHistory[0] : null;
  const lastCompletedLog = logHistory.find((log) => log.is_complete) ?? null;
  const accent = STATUS_COLOR_CLASSES[inspection.status];

  return (
    <Card className={cn('flex flex-col overflow-hidden p-0 border-l-4', accent.borderLeft)}>
      <CardHeader className={cn('px-2 py-2', accent.headerBg)}>
        <div className="flex items-start gap-2">
          <div className="mt-0.5 shrink-0">
            <StatusIndicator
              statusIsLate={inspection.is_past_due}
              statusIsFailed={inspection.has_failed}
              statusIsWarning={inspection.has_warning && !inspection.has_failed}
              statusIsOk={statusIsOk}
            />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold leading-snug">
              <GatedLink
                policy="equipment.maintenance.inspections.update"
                className="hover:underline h-auto text-foreground text-sm font-semibold leading-snug"
                to={`/equipment/maintenance/inspections/${inspection.id}`}
              >
                {inspection.name}
              </GatedLink>
              {inspection.description && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <QuestionMarkCircleIcon className="inline-block w-4 h-4 ml-1 text-foreground/40" />
                    </TooltipTrigger>
                    <TooltipContent>{inspection.description}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </CardTitle>
            {ruleDisplay && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-medium">
                {ruleDisplay}
              </span>
            )}
          </div>
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="px-0 py-0 mt-1 divide-y divide-border/60">
        <div className="grid grid-cols-[3.5rem_1fr] items-center gap-x-2 px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Last
          </span>
          {inspection.last_performed_at ? (
            <DateUsageIndicator
              date={inspection.last_performed_at}
              className={lastCompletedLog ? 'hover:bg-foreground/10 cursor-pointer' : undefined}
              onClick={
                lastCompletedLog
                  ? () =>
                      navigate({
                        to: `/fs/equipment/inspections/${inspection.id}/inspection-logs/${lastCompletedLog.id}`,
                      })
                  : undefined
              }
            />
          ) : (
            <span className="px-1.5 py-0.5 border rounded border-border bg-muted/50 font-mono text-xs text-muted-foreground/50 italic">
              Never
            </span>
          )}
        </div>
        <div className="grid grid-cols-[3.5rem_1fr] items-center gap-x-2 px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Next
          </span>
          <DateUsageIndicator
            date={inspection.is_past_due ? inspection.last_expected_date : inspection.next_due_date}
            is_past_due_time={inspection.is_past_due}
          />
        </div>
      </CardContent>

      <PolicyGate policy="equipment.maintenance.inspections.complete">
        <CardFooter className="p-0 gap-0 flex">
          {incompleteLog && (
            <Button
              className="flex-1 rounded-none border-t border-r bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-foreground shadow-none"
              onClick={() =>
                navigate({
                  to: `/fs/equipment/inspections/${inspection.id}/inspection-logs/${incompleteLog.id}`,
                })
              }
            >
              <ArrowPathIcon className="w-4 h-4 mr-1.5" />
              Continue
            </Button>
          )}
          <Button
            className="flex-1 rounded-none border-t bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-foreground shadow-none"
            onClick={() =>
              navigate({
                to: `/fs/equipment/inspections/${inspection.id}/inspection-logs/new`,
              })
            }
          >
            <PlayIcon className="w-4 h-4 mr-1.5" />
            Start
          </Button>
        </CardFooter>
      </PolicyGate>
    </Card>
  );
};
